import {
  Check,
  Trash2,
  Ban,
  CheckCircle,
  Clock,
  ChevronDown,
  Edit,
  Eye,
} from "lucide-react";
import { useState } from "react";
import { manageNews } from "../../../services/newsServices";
import toast from "react-hot-toast";
import DeleteConfirmationModal from "../members/DeleteMemberModal";
import { useNavigate } from "react-router";
import { getCookie } from "../../../utils";

export const NewsListTable = ({
  news = [],
  refetch,
  startIndex = 0,
  isHide = false,
  permissionsData,
}) => {
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);

  const handleAction = async (id, action, status) => {
    try {
      await manageNews({ id, action, status });
      toast.success(
        `News ${
          action === "delete" ? "deleted" : "status updated"
        } successfully`
      );
      refetch();
    } catch (error) {
      toast.error(error.message || "Action failed");
    }
  };

  const handleStatusChange = async (newsItem, newStatus) => {
    try {
      await manageNews({
        id: newsItem._id,
        action: "update",
        status: newStatus,
      });
      toast.success(`Status updated to ${newStatus}`);
      refetch();
      setOpenDropdown(null);
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const openDeleteModal = (newsItem) => {
    setSelectedNews(newsItem);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedNews) return;
    await handleAction(selectedNews._id, "delete");
    setDeleteModalOpen(false);
    setSelectedNews(null);
  };

  const openEdit = (id) => {
    window.location.href = `/admin/news/edit-news?newsId=${id}`;
  };

  const role = getCookie("role");

  let canEdit = false;
  let canDelete = false;
  let canView = false;

  if (role === "admin") {
    canEdit = true;
    canDelete = true;
    canView = true;
  } else if (role === "maintainer") {
    const modulePermissions = permissionsData?.permissions?.find(
      (p) => p.module === "News"
    );
    canEdit = !!modulePermissions?.edit;
    canDelete = !!modulePermissions?.delete;
    canView = !!modulePermissions?.view;
  }

  const showActions = canEdit || canDelete || canView;

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return {
          icon: <CheckCircle size={14} />,
          bgColor: "bg-green",
          textColor: "text-white",
          hoverColor: "hover:bg-green-600",
          label: "Approved",
        };
      case "rejected":
        return {
          icon: <Ban size={14} />,
          bgColor: "bg-red",
          textColor: "text-white",
          hoverColor: "hover:bg-red-600",
          label: "Rejected",
        };
      case "pending":
      default:
        return {
          icon: <Clock size={14} />,
          bgColor: "bg-yellow-500",
          textColor: "text-white",
          hoverColor: "hover:bg-yellow-600",
          label: "Pending",
        };
    }
  };

  const StatusDropdown = ({ newsItem, currentStatus }) => {
    const isOpen = openDropdown === newsItem._id;
    const statusConfig = getStatusConfig(currentStatus);

    const statusOptions = [
      {
        value: "pending",
        label: "Pending",
        icon: <Clock size={14} />,
        color: "text-yellow-600",
      },
      {
        value: "approved",
        label: "Approved",
        icon: <CheckCircle size={14} />,
        color: "text-green",
      },
      {
        value: "rejected",
        label: "Rejected",
        icon: <Ban size={14} />,
        color: "text-red",
      },
    ];

    return (
      <div className="relative">
        <button
          onClick={() => setOpenDropdown(isOpen ? null : newsItem._id)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${statusConfig.bgColor} ${statusConfig.textColor} ${statusConfig.hoverColor}`}
        >
          {statusConfig.icon}
          <span className="capitalize">{statusConfig.label}</span>
          <ChevronDown
            size={12}
            className={`transform transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setOpenDropdown(null)}
            />
            <div className="absolute top-full left-0 mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-20">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleStatusChange(newsItem, option.value)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    currentStatus?.toLowerCase() === option.value
                      ? "bg-gray-100"
                      : ""
                  }`}
                  disabled={currentStatus?.toLowerCase() === option.value}
                >
                  <span className={option.color}>{option.icon}</span>
                  <span className="text-gray-700 capitalize">
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl h-full shadow-sm overflow-hidden">
        <table className="min-w-full table-fixed text-sm border border-gray-300">
          <thead className="bg-[#4F22D8] text-white text-base">
            <tr>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400">
                No
              </th>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400 w-[300px]">
                Title
              </th>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400 w-[200px]">
                Created By
              </th>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400">
                Date
              </th>
              <th className="py-3 px-4 text-left font-bold border-r border-gray-400">
                Status
              </th>
              {showActions && (
                <th className="py-3 px-4 text-left font-bold">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {news.length === 0 ? (
              <tr>
                <td
                  colSpan={showActions ? 6 : 5}
                  className="text-center text-gray-500 py-6 border-t border-gray-300"
                >
                  No news found.
                </td>
              </tr>
            ) : (
              news.map((row, idx) => (
                <tr
                  key={row._id}
                  className="border-t border-gray-300 text-[15px] hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 pl-7 font-normal text-black border-r border-gray-300">
                    {startIndex + idx + 1}
                  </td>
                  <td className="px-4 py-3 font-normal text-black border-r border-gray-300 leading-snug w-[300px]">
                    {row.title}
                  </td>
                  <td className="px-1 py-3 font-normal text-black border-r border-gray-300 w-[220px]">
                    <div className="flex items-center gap-2">
                      {row.memberId ? (
                        <>
                          <img
                            src={row.memberId.profileInfo.photo}
                            alt={`${row.memberId.profileInfo.firstName} ${row.memberId.profileInfo.lastName}`}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                          <div>
                            <div className="font-semibold text-sm">
                              {row.memberId.profileInfo.firstName}{" "}
                              {row.memberId.profileInfo.lastName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {row.memberId.organization.organizationName}
                            </div>
                          </div>
                        </>
                      ) : (
                        <span className="font-medium">Admin</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-black border-r border-gray-300">
                    {new Date(row.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 border-r border-gray-300">
                    {isHide ? (
                      <span className="capitalize">
                        {row.status || "pending"}
                      </span>
                    ) : canEdit ? (
                      <StatusDropdown
                        newsItem={row}
                        currentStatus={row.status}
                      />
                    ) : (
                      <span className="capitalize">
                        {row.status || "pending"}
                      </span>
                    )}
                  </td>
                  {showActions && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {canView && (
                          <button
                            onClick={() =>
                              navigate(
                                `/admin/news/view-details/${
                                  row._id
                                }/${encodeURIComponent(row.title)}`
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 font-semibold text-xs flex items-center gap-1 transition-colors"
                          >
                            <Eye size={14} /> View
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => openEdit(row._id)}
                            className="text-[#4F22D8] font-semibold text-xs flex items-center gap-1"
                          >
                            <Edit size={14} /> Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="text-red hover:text-red font-medium flex items-center gap-1 transition-colors"
                            onClick={() => openDeleteModal(row)}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedNews && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onRequestClose={() => setDeleteModalOpen(false)}
          onDelete={confirmDelete}
          itemName={selectedNews.title}
          itemType="News"
        />
      )}
    </>
  );
};
